<template>
  <div
    v-if="showChat"
    :class="[
      'chat-dialog',
      {
        'chat-fullscreen': isFullscreen,
        'chat-minimized': isMinimized,
        'guide-chat': isGuideChat,
      },
    ]"
  >
    <!-- Cabeçalho do Chat -->
    <div class="chat-header">
      <div
        class="chat-title"
        @click.stop="toggleMinimize"
        style="display: flex; align-items: center; gap: 10px"
      >
        <img
          src="https://content.professorlucassilva.com.br/lms/images/shark-gpt.svg"
          alt="SharkGPT Logo"
          style="
            height: 40px;
            width: 40px;
            background: white;
            border-radius: 20px;
            padding: 8px;
          "
        />
        SharkGPT
      </div>
      <div class="actions">
        <template v-if="isMinimized && !isMobile">
          <button @click="toggleMinimize" class="action-btn">
            <span class="material-icons">open_in_full</span>
          </button>
          <button @click="closeChat" class="action-btn">
            <span class="material-icons">close</span>
          </button>
        </template>

        <template v-else>
          <template v-if="!isMobile">
            <button @click="toggleMinimize" class="action-btn">
              <span class="material-icons">remove</span>
            </button>
            <button @click="toggleFullscreen" class="action-btn">
              <span class="material-icons">{{
                isFullscreen ? "close_fullscreen" : "open_in_full"
              }}</span>
            </button>
          </template>
          <!-- Botão de fechar sempre visível -->
          <button @click="closeChat" class="action-btn">
            <span class="material-icons">close</span>
          </button>
        </template>
      </div>
    </div>

    <!-- Corpo do Chat -->
    <div v-show="!isMinimized" class="chat-body">
      <div class="messages" ref="messageContainer">
        <div v-if="isInitializing" class="loading-container">
          <div class="loading-spinner"></div>
          <div class="loading-text">Inicializando chat...</div>
        </div>
        <div v-else>
          <div
            v-for="(message, index) in messages"
            :key="index"
            :class="[
              'message',
              message.sender === 'student'
                ? 'message-student'
                : 'message-tutor',
            ]"
          >
            <div class="message-content">
              <div v-html="message.content"></div>
              <span class="message-time">{{
                formatTime(message.timestamp)
              }}</span>
            </div>
          </div>
          <div v-if="streamingMessage" class="message message-tutor">
            <div class="message-content">
              <div v-html="compiledMarkdown(streamingMessage)"></div>
              <span class="message-time">{{ formatTime(new Date()) }}</span>
            </div>
          </div>
          <div v-if="isTyping" class="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>

      <!-- Área de Input -->
      <div class="chat-input">
        <textarea
          v-model="newMessage"
          @input="handleUserTyping"
          @keyup.enter.prevent="sendMessage"
          :disabled="chatAvailable === false || isInitializing"
          :placeholder="
            isInitializing ? 'Inicializando chat...' : 'Digite sua mensagem...'
          "
          rows="1"
        ></textarea>
        <button
          @click="sendMessage"
          class="send-btn"
          :disabled="isInitializing"
        >
          <span class="material-icons">send</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Echo from "@ably/laravel-echo";
import MarkdownIt from "markdown-it";
import TutorService from "../../services/tutor.service";
import { mapGetters } from "vuex";

export default {
  name: "ChatDialog",
  props: {
    courseId: {
      type: Number,
      required: true,
    },
    isGuideChat: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chatInitialized: false,
      showChat: false,
      chatAvailable: true,
      isFullscreen: false,
      isMinimized: false,
      isTyping: false,
      chatAssistant: null,
      newMessage: "",
      messages: [],
      threadId: null,
      typingTimer: null,
      pendingMessages: [],
      isUserTyping: false,
      lastTypingTime: 0,
      isMobile: window.innerWidth <= 768,
      isInitializing: false,
      streamingMessage: "",
      partialMessageBuffer: "",
      listBuffer: "",
      isBufferingList: false,
    };
  },
  watch: {
    courseId: {
      async handler() {
        await this.initComponent();
      },
      immediate: true,
    },
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
      deep: true,
    },
    // Observa mudanças no estado de digitação
    isTyping(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
  },

  computed: {
    ...mapGetters({
      user: "authUser",
      isChatActive: "isChatActive",
      isChatMinimized: "isChatMinimized",
    }),
  },

  created() {
    // Adiciona listener para atualizar isMobile quando a tela for redimensionada
    window.addEventListener("resize", this.checkMobile);
  },
  beforeDestroy() {
    // Remove o listener quando o componente for destruído
    window.removeEventListener("resize", this.checkMobile);
  },

  async mounted() {
    await this.initComponent();
  },

  methods: {
    async initComponent() {
      if (this.chatAssistant === null && Number.isFinite(this.courseId)) {
        try {
          const response = await TutorService.getChatAssistantByCourseId(
            this.courseId,
          );
          if (response.data.length > 0) {
            this.chatAssistant = response.data[0];
            this.chatAvailable = true;
          } else {
            this.chatAssistant = null;
            this.chatAvailable = false;
          }
        } catch (error) {
          this.chatAssistant = null;
          this.chatAvailable = false;
        }
      }

      if (this.chatAvailable) {
        if (this.isChatActive) {
          this.showChat = true;
          this.initializeChat();
        }
        this.isMinimized = this.isChatMinimized;

        // Adiciona listeners para ambos os eventos do drawer
        window.getApp.$on(
          "APP_DRAWER_CONTENT_TOGGLED",
          this.handleDrawerToggle,
        );
        window.getApp.$on("APP_DRAWER_TOGGLED", this.handleDrawerToggle);

        this.scrollToBottom();
        this.registerMenuOptions();
      }
    },
    registerMenuOptions() {
      if (this.showChat) {
        this.$emit("register-options", []);
        return;
      }

      const options = [
        {
          label: "Falar com o SharkGPT",
          component: "chatDialog",
          action: "startChat",
          order: 3,
        },
      ];

      this.$emit("register-options", options);
    },

    addMessageToChat(message) {
      this.messages.push({
        content: this.compiledMarkdown(message.content),
        sender: message.sender,
        timestamp: message.timestamp ?? new Date(),
      });
      this.scrollToBottom();
    },
    addTutorMessageToChat(messageBody) {
      this.addMessageToChat({
        content: messageBody,
        sender: "tutor",
      });
      this.isTyping = false;
    },

    processStreamChunk(chunk, isFinalMessage) {
      this.isTyping = true;
      this.streamingMessage += chunk;

      this.partialMessageBuffer += chunk;
      this.scrollToBottom();

      if (isFinalMessage) {
        this.addTutorMessageToChat(this.streamingMessage);
        this.streamingMessage = "";
        this.isTyping = false;
      }
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      if (this.isFullscreen) {
        this.isMinimized = false;
      }
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
      this.$store.dispatch("setChatMinimized", this.isMinimized);

      if (this.isMinimized) {
        this.isFullscreen = false;
        if (this.isMobile) {
          document.body.style.overflow = "";
        }
      } else {
        // Quando maximiza o chat
        if (this.isMobile) {
          this.isFullscreen = true;
          document.body.style.overflow = "hidden";
          // Aguarda a transição do CSS terminar antes de rolar
          setTimeout(() => {
            this.scrollToBottom();
          }, 300); // 300ms é o tempo da transição CSS
        }
        // Se não for mobile, rola imediatamente
        else {
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      }
    },
    async handleUserTyping() {
      this.isUserTyping = true;
      this.lastTypingTime = Date.now();

      if (this.typingTimer) {
        clearTimeout(this.typingTimer);
      }

      this.typingTimer = setTimeout(async () => {
        this.isUserTyping = false;
        await this.sendPendingMessages();
      }, 2000);
    },
    async sendPendingMessages() {
      if (this.pendingMessages.length > 0) {
        let messageBody = this.pendingMessages
          .map((message) => message.content)
          .join("\n");
        try {
          this.isTyping = true;
          await TutorService.sendUserMessage(this.threadId, messageBody);
        } catch (error) {
          this.addTutorMessageToChat(
            "Desculpe, houve um erro ao enviar a mensagem. Por favor, tente novamente mais tarde.",
          );
          this.chatAvailable = false;
          this.isInitializing = false;
        }
        this.pendingMessages = [];
      }
    },
    sendMessage() {
      if (!this.newMessage.trim()) return;

      this.pendingMessages.push({
        content: this.newMessage,
        sender: "student",
        timestamp: new Date(),
      });

      this.addMessageToChat({
        content: this.newMessage,
        sender: "student",
      });
      this.newMessage = "";
      if (!this.isUserTyping) {
        this.sendPendingMessages();
      }
    },
    formatTime(date) {
      return new Date(date).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    scrollToBottom() {
      const container = this.$refs.messageContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    handleImageError(e) {
      // Fallback para uma imagem padrão em caso de erro
    },
    compiledMarkdown(text) {
      const md = new MarkdownIt({
        html: true, // Permite HTML nas mensagens
        linkify: true, // Converte URLs em links
        typographer: true, // Habilita algumas substituições de texto
        breaks: true, // Converte quebras de linha em <br>
      });

      const defaultRender =
        md.renderer.rules.link_open ||
        function (tokens, idx, options, env, self) {
          return self.renderToken(tokens, idx, options);
        };

      md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
        const token = tokens[idx];
        const aIndex = token.attrIndex("target");
        if (aIndex < 0) {
          token.attrPush(["target", "_blank"]);
        } else {
          token.attrs[aIndex][1] = "_blank";
        }
        return defaultRender(tokens, idx, options, env, self);
      };

      // Adiciona classes para garantir consistência visual
      md.renderer.rules.paragraph_open = function (
        tokens,
        idx,
        options,
        env,
        self,
      ) {
        return '<span class="markdown-paragraph">';
      };

      md.renderer.rules.list_item_open = function (
        tokens,
        idx,
        options,
        env,
        self,
      ) {
        return '<li class="markdown-list-item">';
      };

      // text = text.replace(/【\d+:\d+†source】/g, '');
      text = text.replace(/【\d+:\d+†source】/gu, "");

      return md.render(text);
    },

    startChat() {
      this.showChat = true;
      this.registerMenuOptions();
      this.$store.dispatch("setChatActive", true);
      this.initializeChat(); // Inicializa o chat quando o usuário clica em iniciar
      if (this.isMobile) {
        this.isFullscreen = true;
        this.isMinimized = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
        // document.body.style.overflow = 'hidden'; // Previne scroll do body
      }
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    closeChat() {
      this.showChat = false;
      this.$store.dispatch("setChatActive", false);
      if (this.isMobile) {
        document.body.style.overflow = ""; // Restaura o scroll do body
      }
      this.registerMenuOptions();
    },

    checkMobile() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 768;

      // Só atualiza o estado se houver mudança de mobile para desktop ou vice-versa
      if (wasMobile !== this.isMobile) {
        if (this.isMobile && this.showChat && !this.isMinimized) {
          this.isFullscreen = true;
          this.$nextTick(() => {
            this.scrollToBottom();
          });
          document.body.style.overflow = "hidden";
        } else {
          document.body.style.overflow = "";
        }
      }
    },

    handleDrawerToggle() {
      // Previne que os eventos do drawer afetem o estado do chat
      if (this.isMobile && this.showChat && !this.isMinimized) {
        this.$nextTick(() => {
          // Restaura o estado do chat
          this.isFullscreen = true;
          document.body.style.overflow = "hidden";
        });
      }
    },

    initializeChat() {
      if (this.chatInitialized) {
        return;
      }

      if (
        this.chatAvailable === false ||
        Number.isFinite(this.courseId) === false
      ) {
        this.showChat = false;
        this.chatAvailable = false;
        return;
      }

      this.isInitializing = true;
      this.chatInitialized = true;
      window.Ably = require("ably");
      window.Echo = new Echo({
        broadcaster: "ably",
        auth: {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      });

      // Register a callback for listing to connection state change
      window.Echo.connector.ably.connection.on((stateChange) => {
        console.log("LOGGER:: Connection event :: ", stateChange);
        if (
          stateChange.current === "disconnected" &&
          stateChange.reason?.code === 40142
        ) {
          console.log(
            "LOGGER:: Connection token expired https://help.ably.io/error/40142",
          );
        }
      });

      // Adiciona listener para erros de autorização do canal
      window.Echo.connector.ably.connection.on("failed", (error) => {
        console.error("LOGGER:: Erro de autorização do canal:", error);
        this.chatAvailable = false;
      });

      TutorService.createThread(this.user.id, this.courseId)
        .then((response) => {
          this.threadId = response.data.id;

          window.Echo.private(`chat.${response.data.id}`)
            .listen(".message.sent", (data) =>
              this.processStreamChunk(data.message, data.is_final_message),
            )
            .error((error) => {
              if (this.chatAvailable) {
                this.addTutorMessageToChat(
                  "Desculpe, houve um erro ao inicializar o chat. Por favor, tente novamente mais tarde.",
                );
                this.chatAvailable = false;
                this.isInitializing = false;
              }
            });

          window.Echo.private(`chat.${response.data.id}`)
            .listen(".chat.unavailable", (data) => {
              this.addTutorMessageToChat(data.message);
              this.chatAvailable = false;
              this.isInitializing = false;
            })
            .error((error) => {
              console.error("LOGGER:: Erro ao inicializar chat:", error);
              if (this.chatAvailable) {
                this.addTutorMessageToChat(
                  "Desculpe, houve um erro ao inicializar o chat. Por favor, tente novamente mais tarde.",
                );
                this.chatAvailable = false;
                this.isInitializing = false;
              }
            });
          if (response.isSuccess()) {
            TutorService.getThreadMessages(response.data.id)
              .then((response) => {
                for (const message of response.data) {
                  this.addMessageToChat({
                    content: message.body,
                    sender: message.sender.toLowerCase(),
                    timestamp: new Date(message.created_at),
                  });
                }
                this.isInitializing = false;
              })
              .catch((error) => {
                console.error("Erro ao pegar mensagens da thread:", error);
                this.isInitializing = false;
              });
          }

          this.chatInitialized = true;
        })
        .catch((error) => {
          if (this.chatAvailable) {
            this.addTutorMessageToChat(
              "Desculpe, houve um erro ao inicializar o chat. Por favor, tente novamente mais tarde.",
            );
            this.chatAvailable = false;
            this.isInitializing = false;
          }
        });
    },
  },
};
</script>

<style scoped>
.chat-dialog {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 350px;
  height: 500px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.3s ease;
  z-index: 1000;
  transform-origin: bottom right;
  font-family: "Roboto", "Segoe UI", Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
}

/* Posição personalizada quando o chat é iniciado a partir do guia de estudos */
.chat-dialog.guide-chat {
  right: auto;
  left: 20px; /* Posiciona à esquerda */
}

.chat-fullscreen {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  border-radius: 0 !important;
  max-width: 100vw !important;
  transform: none !important;
  z-index: 9999 !important;
}

.chat-minimized {
  height: 60px;
}

.chat-header {
  padding: 15px;
  background: #2196f3;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: inherit;
}

.chat-title {
  font-weight: 500;
  font-size: 1.1em;
  font-family: inherit;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
}

.actions {
  display: flex;
  gap: 5px;
}

.action-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

.action-btn .material-icons {
  font-size: 20px;
}

.chat-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.3s ease;
}

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  scroll-behavior: smooth; /* Adiciona rolagem suave */
}

.message {
  max-width: 80%;
  padding: 8px 8px 2px 8px;
  border-radius: 8px;
  margin: 5px 0;
  position: relative;
}

.message-student {
  align-self: flex-end;
  background: #e3f2fd;
  margin-left: 20%;
}

.message-tutor {
  align-self: flex-start;
  background: #f5f5f5;
  margin-right: 20%;
}

.message-content {
  position: relative;
  padding-bottom: 10px;
  word-break: break-word;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

/* Estilos para elementos HTML dentro das mensagens */
.message-content :deep(strong) {
  font-weight: bold;
}

.message-content :deep(em) {
  font-style: italic;
}

.message-content :deep(a) {
  color: #2196f3;
  text-decoration: underline;
}

.message-content :deep(code) {
  background-color: #f5f5f5;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: "Roboto Mono", "Consolas", monospace;
  font-size: 0.9em;
}

.message-content :deep(pre) {
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 5px;
  overflow-x: auto;
  font-family: "Roboto Mono", "Consolas", monospace;
  font-size: 0.9em;
  line-height: 1.4;
}

.message-content :deep(ul),
.message-content :deep(ol) {
  padding-left: 20px;
  margin: 5px 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

.message-content :deep(li) {
  margin-bottom: 5px;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

.message-content :deep(.markdown-paragraph) {
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  color: inherit;
  margin: 5px 0;
}

.message-content :deep(blockquote) {
  border-left: 3px solid #2196f3;
  padding-left: 10px;
  margin: 5px 0;
  color: #666;
  font-style: italic;
  font-family: inherit;
}

.message-time {
  position: absolute;
  bottom: 0;
  right: 0;
  font-size: 0.7em;
  color: #666;
  font-family: inherit;
}

.chat-input {
  padding: 15px;
  background: #f8f9fa;
  display: flex;
  gap: 10px;
}

textarea {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 20px;
  padding: 10px 15px;
  resize: none;
  outline: none;
  font-family: inherit;
  font-size: inherit;
}

.send-btn {
  background: #2196f3;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.send-btn .material-icons {
  font-size: 20px;
}

.typing-indicator {
  display: flex;
  gap: 5px;
  padding: 10px;
  align-self: flex-start;
  font-family: inherit;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #90caf9;
  border-radius: 50%;
  animation: typing 1s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

.text-div-fixed {
  background: -webkit-linear-gradient(
    75deg,
    var(--v-primary-lighten2),
    var(--v-primary-lighten3)
  );
  border-radius: 9999px;
  padding: 3px 25px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
}

@keyframes typing {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  gap: 10px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #2196f3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: #666;
  font-size: 0.9em;
  font-family: inherit;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.send-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

textarea:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .chat-minimized {
    height: 60px;
    width: 200px !important;
    bottom: 20px !important;
    border-radius: 12px !important;
    right: 20px !important;
    left: auto !important;
  }

  .guide-chat.chat-minimized {
    right: auto !important;
    left: 20px !important;
  }

  .chat-dialog:not(.chat-minimized) {
    width: 100vw !important;
    height: 100vh !important;
    bottom: 0 !important;
    right: 0 !important;
    left: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border-radius: 0 !important;
    position: fixed !important;
  }

  .chat-fullscreen {
    width: 100vw !important;
    height: 100vh !important;
    margin: 0 !important;
    padding: 0 !important;
    left: 0 !important;
    right: 0 !important;
    top: 0 !important;
    bottom: 0 !important;
  }

  .chat-title {
    flex: 1;
  }
}
</style>
